// The expert commission's desk: cases whose retention has ended (or ends
// soon, or that a person must decide on), and the destruction acts made from
// them. An act is generated as a document, signed with E-IMZO by the
// commission, approved, and only then executed — by someone holding
// records:dispose, who types the act's number. Execution destroys the
// records' content for good; their tombstones, the cases' cards and the act
// stay.

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Archive, CheckCircle2, Download, FileText, Flame, Loader2, Lock, RefreshCw, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { ConfirmDialog } from '@/modules/console/components/ConfirmDialog.jsx';
import { ExtendDialog, ReasonDialog } from './dialogs.jsx';
import { DocumentEditorModal } from '@/shared/ui/DocumentEditorModal.jsx';
import { records } from '../lib/api.js';
import { fmtDay } from '../lib/journals.js';
import { periodLabel } from '../lib/retention.js';
import { LIMITS } from '../lib/validation.js';

const ACT_TONE = {
  draft: 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
  approved: 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
  executed: 'border-border bg-muted text-foreground',
  cancelled: 'border-border bg-muted/50 text-muted-foreground',
};

function ExecuteDialog({ act, onOpenChange, onDone }) {
  const { t } = useTranslation('records');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => setTyped(''), [act]);
  const submit = async () => {
    setBusy(true);
    const res = await records.executeAct(act.uuid, typed.trim());
    setBusy(false);
    if (res.success) {
      toast.success(t('acts.executed', { number: act.number }));
      onDone?.();
      onOpenChange(false);
    } else {
      toast.error(res.message);
    }
  };
  return (
    <Dialog open={Boolean(act)} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md" role="alertdialog">
        <DialogHeader>
          {act?.kind === 'transfer' ? (
            <>
              <DialogTitle className="flex items-center gap-2"><Archive className="h-5 w-5" /> {t('acts.transferTitle', { number: act?.number })}</DialogTitle>
              <DialogDescription>{t('acts.transferDescription', { cases: act?.cases.length || 0, records: act?.records || 0 })}</DialogDescription>
            </>
          ) : (
            <>
              <DialogTitle className="flex items-center gap-2 text-red-700 dark:text-red-300"><Flame className="h-5 w-5" /> {t('acts.executeTitle', { number: act?.number })}</DialogTitle>
              <DialogDescription>{t('acts.executeDescription', { cases: act?.cases.length || 0, records: act?.records || 0 })}</DialogDescription>
            </>
          )}
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="execute-confirm">{t('acts.typeNumber', { number: act?.number })}</Label>
          <Input id="execute-confirm" required maxLength={LIMITS.actConfirm} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('actions.cancel')}</Button>
          <Button variant={act?.kind === 'transfer' ? 'default' : 'destructive'} onClick={submit} disabled={busy || typed.trim() !== act?.number} data-testid="execute-confirm">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t(act?.kind === 'transfer' ? 'acts.handOver' : 'acts.execute')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActCard({ act, access, onChanged }) {
  const { t, i18n } = useTranslation('records');
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null); // 'approve' | 'cancel' | 'execute'
  const [opened, setOpened] = useState(null);
  const draft = act.status === 'draft';
  const docUsable = act.document_file_uuid && act.document_status !== 'missing';
  const canRemake = draft && ['none', 'missing', 'editable'].includes(act.document_status);

  const run = async (key, call, message) => {
    setBusy(key);
    const res = await call();
    setBusy(null);
    if (res.success) {
      if (message) toast.success(message);
      onChanged();
    } else {
      toast.error(res.message);
    }
    return res;
  };

  return (
    <div className="space-y-3 rounded-md border p-3" data-testid={`act-${act.number}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium">{t(act.kind === 'transfer' ? 'acts.transferAct' : 'acts.title', { number: act.number })}</p>
          <p className="text-xs text-muted-foreground">
            {t('acts.summary', { cases: act.cases.length, records: act.records })}
            {act.executed_at && ` · ${t('acts.executedOn', { date: fmtDay(act.executed_at) })}`}
            {act.cancel_reason && ` · ${act.cancel_reason}`}
          </p>
        </div>
        <Badge variant="outline" className={ACT_TONE[act.status]}>{t(`acts.statuses.${act.status}`)}</Badge>
      </div>

      <ul className="divide-y rounded-md border text-sm">
        {act.cases.map((c) => (
          <li key={c.case_uuid} className="flex items-center gap-3 px-3 py-1.5">
            <span className="w-16 shrink-0 font-mono text-xs">{c.index}</span>
            <span className="flex-1">{c.title} <span className="text-xs text-muted-foreground">· {c.year}</span></span>
            <span className="text-xs text-muted-foreground">{periodLabel(c, t)}</span>
            {c.legal_hold && <Badge variant="outline" className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">{t('review.onHold')}</Badge>}
            {draft && access.can_review && act.cases.length > 1 && (
              <Button variant="ghost" size="icon" className="h-6 w-6" aria-label={t('acts.removeCase')}
                onClick={() => run('remove', () => records.removeActCase(act.uuid, c.case_uuid))}>
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </li>
        ))}
      </ul>

      {act.status !== 'executed' && act.status !== 'cancelled' && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="flex-1">
            {docUsable ? t(`plan.document.${act.document_status}`, { defaultValue: act.document_status }) : t('acts.noDocument')}
            {' · '}{t('acts.signatures', { present: act.signatures_present, needed: act.signatures_needed })}
            {draft && act.approve_blocked_by && <span className="block text-muted-foreground" data-testid="act-blocked">{t(`acts.blocked.${act.approve_blocked_by}`)}</span>}
          </span>
          {docUsable && (
            <Button variant="outline" size="sm" onClick={() => setOpened({ uuid: act.document_file_uuid, name: t('acts.title', { number: act.number }) })}>
              {t('plan.openDocument')}
            </Button>
          )}
          {access.can_review && canRemake && (
            <Button variant="outline" size="sm" disabled={busy === 'document'} data-testid="act-make-document"
              onClick={() => run('document', () => records.actDocument(act.uuid, i18n.language), t('acts.documentMade'))}>
              {busy === 'document' && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              {t(act.document_file_uuid ? 'plan.remakeDocument' : 'plan.makeDocument')}
            </Button>
          )}
          {access.can_review && draft && (
            <Button size="sm" className="gap-1.5" disabled={!act.can_approve} onClick={() => setConfirm('approve')} data-testid="act-approve">
              <CheckCircle2 className="h-3.5 w-3.5" /> {t('acts.approve')}
            </Button>
          )}
          {act.status === 'approved' && act.kind !== 'transfer' && access.can_dispose && (
            <Button size="sm" variant="destructive" className="gap-1.5" onClick={() => setConfirm('execute')} data-testid="act-execute">
              <Flame className="h-3.5 w-3.5" /> {t('acts.execute')}
            </Button>
          )}
          {act.status === 'approved' && act.kind === 'transfer' && access.can_review && (
            <Button size="sm" className="gap-1.5" onClick={() => setConfirm('execute')} data-testid="act-execute">
              <Archive className="h-3.5 w-3.5" /> {t('acts.handOver')}
            </Button>
          )}
          {access.can_review && (
            <Button size="sm" variant="ghost" onClick={() => setConfirm('cancel')}>{t('acts.cancel')}</Button>
          )}
        </div>
      )}

      {act.kind === 'transfer' && act.status === 'executed' && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="act-package">
          <Archive className="h-4 w-4 text-muted-foreground" />
          <span className="flex-1">
            {t(`acts.package.${act.package_status || 'building'}`, { entries: act.package_entries || 0 })}
            {act.package_error && <span className="block text-red-700 dark:text-red-300">{act.package_error}</span>}
            {act.package_sha256 && <span className="block font-mono text-[10px] text-muted-foreground">SHA-256 {act.package_sha256.slice(0, 16)}…</span>}
          </span>
          {act.package_status === 'ready' && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={async () => {
              const res = await records.packageLink(act.uuid);
              if (res.success) window.open(res.response.url, '_blank', 'noopener');
              else toast.error(res.message);
            }} data-testid="act-download">
              <Download className="h-3.5 w-3.5" /> {t('acts.download')}
            </Button>
          )}
          {act.package_status === 'failed' && access.can_review && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => run('rebuild', () => records.rebuildPackage(act.uuid), t('acts.rebuilding'))}>
              <RefreshCw className="h-3.5 w-3.5" /> {t('acts.rebuild')}
            </Button>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirm === 'approve'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={t('acts.approveTitle', { number: act.number })}
        description={t('acts.approveDescription')}
        confirmLabel={t('acts.approve')}
        onConfirm={() => run('approve', () => records.approveAct(act.uuid), t('acts.approved', { number: act.number }))}
      />
      <ReasonDialog
        open={confirm === 'cancel'}
        title={t('acts.cancelTitle', { number: act.number })}
        description={t('acts.cancelDescription')}
        confirmLabel={t('acts.cancel')}
        onOpenChange={(o) => !o && setConfirm(null)}
        onConfirm={(reason) => run('cancel', () => records.cancelAct(act.uuid, reason), t('acts.cancelled'))}
      />
      <ExecuteDialog act={confirm === 'execute' ? act : null} onOpenChange={(o) => !o && setConfirm(null)} onDone={onChanged} />
      {opened && <DocumentEditorModal open doc={opened} onClose={() => { setOpened(null); onChanged(); }} onSave={onChanged} />}
    </div>
  );
}

export function ReviewView({ access }) {
  const { t } = useTranslation('records');
  const [due, setDue] = useState(null);
  const [acts, setActs] = useState(null);
  const [toTransfer, setToTransfer] = useState([]);
  const [health, setHealth] = useState(null);
  const [picked, setPicked] = useState([]);
  const [pickedTransfer, setPickedTransfer] = useState([]);
  const [extending, setExtending] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [queue, list, transfers, integrity] = await Promise.all([
      records.review(), records.acts(), records.transferQueue(), records.integrity(),
    ]);
    if (queue.success) setDue(queue.response);
    else toast.error(queue.message);
    if (list.success) setActs(list.response);
    if (transfers.success) setToTransfer(transfers.response);
    if (integrity.success) setHealth(integrity.response);
    setPicked([]);
    setPickedTransfer([]);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (caseUuid) => setPicked((prev) => (prev.includes(caseUuid) ? prev.filter((x) => x !== caseUuid) : [...prev, caseUuid]));
  const prepare = async (kind = 'destruction') => {
    setBusy(true);
    const res = await records.createAct(kind === 'transfer' ? pickedTransfer : picked, null, kind);
    setBusy(false);
    if (res.success) {
      toast.success(t('acts.created', { number: res.response.number }));
      load();
    } else {
      toast.error(res.message);
    }
  };
  const selectable = (c) => c.due && !c.legal_hold_reason;

  if (!due) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6" data-testid="review-view">
      {health && health.records > 0 && (
        <section className={cn('rounded-md border p-3 text-sm', health.problems.length ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20' : 'bg-muted/40')} data-testid="integrity">
          <p className="flex items-center gap-2 font-medium">
            <ShieldCheck className="h-4 w-4" /> {t('integrity.title')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('integrity.summary', { checked: health.checked, records: health.records, ok: health.ok })}
          </p>
          {health.problems.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-red-800 dark:text-red-300">
              {health.problems.map((p) => (
                <li key={p.record_uuid}>
                  <span className="font-mono">{p.case_index}</span> · {p.name} — {p.fixity_error
                    || t(`integrity.signatures.${p.signatures_status}`, { defaultValue: p.signatures_status })}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold">{t('review.title')}</h2>
            <p className="text-xs text-muted-foreground">{t('review.description')}</p>
          </div>
          {access.can_review && (
            <Button size="sm" className="gap-1.5" disabled={busy || picked.length === 0} onClick={() => prepare()} data-testid="act-create">
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {t('review.prepare', { count: picked.length })}
            </Button>
          )}
        </div>
        {due.length === 0 ? (
          <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="review-empty">{t('review.empty')}</p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="w-8 px-3 py-2" />
                  <th className="px-3 py-2">{t('case.index')}</th>
                  <th className="px-3 py-2">{t('case.title')}</th>
                  <th className="px-3 py-2">{t('case.retention')}</th>
                  <th className="px-3 py-2">{t('case.keptUntil')}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {due.map((c) => (
                  <tr key={c.uuid} className={cn('border-t', !c.due && 'text-muted-foreground')} data-testid={`due-${c.case_index}`}>
                    <td className="px-3 py-2">
                      {access.can_review && (
                        <Checkbox checked={picked.includes(c.uuid)} disabled={!selectable(c)} onCheckedChange={() => toggle(c.uuid)}
                          aria-label={c.case_index} />
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{c.case_index}</td>
                    <td className="px-3 py-2">
                      {c.title} <span className="text-xs text-muted-foreground">· {c.year}</span>
                      <span className="mt-0.5 flex flex-wrap gap-1">
                        {c.epk && <Badge variant="outline" className="border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300">{t('period.epk')}</Badge>}
                        {c.undated && <Badge variant="outline">{t('review.undated')}</Badge>}
                        {c.legal_hold_reason && (
                          <Badge variant="outline" className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300" title={c.legal_hold_reason}>
                            <Lock className="mr-1 h-3 w-3" /> {t('review.onHold')}
                          </Badge>
                        )}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs">{periodLabel(c.retention, t)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs">
                      {c.retention_until ? fmtDay(c.retention_until) : '—'}
                      {!c.due && <span className="block text-amber-700 dark:text-amber-300">{t('review.soon')}</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {access.can_review && (
                        <Button variant="ghost" size="sm" onClick={() => setExtending(c)}>{t('review.extend')}</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {toTransfer.length > 0 && (
        <section className="space-y-2" data-testid="transfer-queue">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-sm font-semibold">{t('transfer.title')}</h2>
              <p className="text-xs text-muted-foreground">{t('transfer.description')}</p>
            </div>
            {access.can_review && (
              <Button size="sm" variant="outline" className="gap-1.5" disabled={busy || pickedTransfer.length === 0}
                onClick={() => prepare('transfer')} data-testid="transfer-create">
                <Archive className="h-3.5 w-3.5" /> {t('transfer.prepare', { count: pickedTransfer.length })}
              </Button>
            )}
          </div>
          <ul className="divide-y rounded-md border text-sm">
            {toTransfer.map((c) => (
              <li key={c.uuid} className="flex items-center gap-3 px-3 py-1.5" data-testid={`transfer-${c.case_index}`}>
                {access.can_review && (
                  <Checkbox checked={pickedTransfer.includes(c.uuid)} aria-label={c.case_index}
                    onCheckedChange={() => setPickedTransfer((prev) => (prev.includes(c.uuid) ? prev.filter((x) => x !== c.uuid) : [...prev, c.uuid]))} />
                )}
                <span className="w-16 shrink-0 font-mono text-xs">{c.case_index}</span>
                <span className="flex-1">{c.title} <span className="text-xs text-muted-foreground">· {c.year} · {t('cases.records')}: {c.items}</span></span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-2">
        <h2 className="text-sm font-semibold">{t('acts.list')}</h2>
        {!acts || acts.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="acts-empty">{t('acts.none')}</p>
        ) : (
          <div className="space-y-3">
            {acts.some((a) => a.status === 'approved') && access.can_dispose && (
              <p className="flex items-center gap-2 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-2 text-xs text-red-800 dark:text-red-300">
                <AlertTriangle className="h-4 w-4" /> {t('acts.readyWarning')}
              </p>
            )}
            {acts.map((act) => <ActCard key={act.uuid} act={act} access={access} onChanged={load} />)}
          </div>
        )}
      </section>

      <ExtendDialog target={extending} onOpenChange={(o) => !o && setExtending(null)} onDone={load} />
    </div>
  );
}
